import type { Schedule, Step, Workflow, WorkflowSpec } from "@/lib/api"

/**
 * Everything a reader needs to judge a workflow at a glance, derived from the
 * spec itself.
 *
 * Derived, not stored: a saved summary drifts the moment the plan is edited,
 * and a model-written one can simply be wrong. Reading the steps is cheap and
 * cannot disagree with what will actually run.
 */

export type Tag = {
  label: string
  /** neutral = descriptive · accent = how it starts · warn = has real effects */
  tone: "neutral" | "accent" | "warn"
  title: string
}

/** Systems a skill id touches, for the "what does this reach" chips. */
const SYSTEM: Record<string, { label: string; title: string }> = {
  "fsp.app_user": { label: "FSP App", title: "Acts as a player in the FSP app — uploads sessions" },
  "fsp.read": { label: "FSP Data", title: "Reads the scoring queue, sessions or verifiers from FSP" },
  slack: { label: "Slack", title: "Posts, reads or manages Slack" },
  "ai.claude": { label: "AI", title: "Asks Claude a question mid-run" },
}

export function triggerLabel(s: Schedule): string {
  if (s.type === "daily") return `Daily ${s.startTime}${s.endTime ? `–${s.endTime}` : ""} ${s.timezone}`
  if (s.type === "interval") return `Every ${s.everyMinutes} min`
  if (s.type === "event") return `On ${s.event.replace(/_/g, " ")}`
  return "Manual"
}

/** Depth-first walk over every step, loops and repeats included. */
export function walkSteps(steps: Step[] = []): Step[] {
  return steps.flatMap((s) =>
    s.kind === "loop" || s.kind === "repeat" ? [s, ...walkSteps(s.steps)] : [s],
  )
}

function fmtDuration(sec: number): string {
  if (sec < 60) return `${sec}s`
  if (sec < 3600) return `${Math.round(sec / 60)} min`
  return `${Math.round((sec / 3600) * 10) / 10} h`
}

export function workflowTags(wf: Workflow): Tag[] {
  const spec = wf.spec ?? ({} as WorkflowSpec)
  const all = walkSteps(spec.steps)
  const tags: Tag[] = []

  tags.push({
    label: triggerLabel(spec.schedule),
    tone: "accent",
    title: spec.schedule?.type === "manual" ? "Runs only when you press Run now" : "How this starts",
  })

  // Systems, in the order they are first reached.
  const seen = new Set<string>()
  for (const s of all) {
    if (s.kind !== "skill") continue
    const sys = SYSTEM[s.skill]
    if (!sys || seen.has(sys.label)) continue
    seen.add(sys.label)
    tags.push({ label: sys.label, tone: "neutral", title: sys.title })
  }

  // The one distinction that actually matters before pressing Run.
  const live = all.some((s) => s.kind === "skill" && s.input?.identity === "live")
  if (live) {
    tags.push({
      label: "Live sessions",
      tone: "warn",
      title: "Creates REAL, non-test sessions in FSP",
    })
  }

  if (all.some((s) => s.kind === "await_event")) {
    tags.push({ label: "Waits for a reply", tone: "neutral", title: "Pauses mid-run for a Slack event" })
  }
  if (all.some((s) => s.kind === "loop" || s.kind === "repeat")) {
    tags.push({ label: "Repeats", tone: "neutral", title: "Contains a loop" })
  }

  return tags
}

/**
 * Plain-language outline: one line per step, in run order.
 *
 * Uses each step's own label — the planner is told to write these as short
 * imperatives — and annotates the structural steps, which have no label worth
 * showing on their own ("wait" tells you nothing; "Wait 5 min" does).
 */
export function workflowOutline(spec: WorkflowSpec | undefined, depth = 0): { text: string; depth: number }[] {
  const out: { text: string; depth: number }[] = []

  const render = (steps: Step[] = [], d: number) => {
    for (const s of steps) {
      if (s.kind === "loop") {
        out.push({ text: `${s.label} — for each of ${s.over.length} (${s.over.join(", ")})`, depth: d })
        render(s.steps, d + 1)
      } else if (s.kind === "repeat") {
        out.push({ text: `${s.label} — ${s.times}×`, depth: d })
        render(s.steps, d + 1)
      } else if (s.kind === "wait") {
        out.push({ text: `Wait ${fmtDuration(s.seconds)}`, depth: d })
      } else if (s.kind === "await_event") {
        const m = s.match ?? {}
        const what = m.reaction ? `a :${m.reaction}: reaction` : s.event.replace(/_/g, " ")
        const where = m.messageTs ? " on the message it just posted" : ""
        out.push({
          text: `${s.label} — waits up to ${fmtDuration(s.timeoutSeconds)} for ${what}${where}`,
          depth: d,
        })
      } else {
        const detail = stepDetail(s.skill, s.input)
        out.push({ text: detail ? `${s.label} — ${detail}` : s.label, depth: d })
      }
    }
  }

  render(spec?.steps, depth)
  return out
}

/**
 * The one line worth showing under a step's title.
 *
 * The graph used to print every input as `key=value key=value`, which was
 * unreadable at node width and truncated anyway. Each skill has one or two
 * fields that actually identify what the step does; show those, humanised,
 * and leave the rest to the detail view.
 */
export function stepDetail(skill: string, input: Record<string, unknown> = {}): string {
  const v = (k: string) => (input[k] === undefined || input[k] === null ? "" : String(input[k]))
  const parts: string[] = []

  switch (skill) {
    case "fsp.app_user": {
      if (v("sport")) parts.push(v("sport"))
      if (v("source") === "random") parts.push("random scored video")
      else if (v("sourceSessionId")) parts.push(`from ${v("sourceSessionId")}`)
      else parts.push("built-in clip")
      parts.push(v("identity") === "live" ? "LIVE session" : "test session")
      break
    }
    case "fsp.read": {
      if (v("dataset")) parts.push(v("dataset").replace(/_/g, " "))
      if (v("sport")) parts.push(v("sport"))
      if (v("order") === "random") parts.push("random")
      if (v("hasMedia") === "true") parts.push("with media")
      if (v("limit")) parts.push(`limit ${v("limit")}`)
      break
    }
    case "slack": {
      const action = v("action") || "send"
      if (action === "send") {
        parts.push(v("threadTs") ? "reply in thread" : `post to ${v("channel") || "Slack"}`)
        if (v("scheduleAtIso")) parts.push("scheduled")
      } else if (action === "read_thread") parts.push(`read thread in ${v("channel")}`)
      else parts.push(action.replace(/_/g, " "))
      break
    }
    case "ai.claude": {
      const p = v("prompt")
      parts.push(p.length > 60 ? p.slice(0, 60) + "…" : p || "ask Claude")
      break
    }
    default: {
      // Unknown skill: fall back to the first couple of inputs rather than
      // showing nothing, so a new skill is still legible before this list
      // learns about it.
      for (const [k, val] of Object.entries(input).slice(0, 2)) parts.push(`${k} ${String(val)}`)
    }
  }

  return parts.filter(Boolean).join(" · ")
}

/** Short, human name for a skill id — used on graph nodes. */
export const SKILL_LABEL: Record<string, string> = {
  "fsp.app_user": "FSP App User",
  "fsp.read": "FSP Data",
  slack: "Slack",
  "ai.claude": "AI",
}
